/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';

import {
  ACTION_BUSINESS_IMAGE_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import { ACTION_BUSINESS_VIEW_PAGE } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AssociatedBusinesses } from 'components/business/landing-page/AssociatedBusinesses';
import { BusinessHomepageJsonLd } from 'jsonld/BusinessHomepageJsonLd';
import { Navigation } from 'types';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { consumer, core } from '@tock/coretypes';
import { getAssociatedBusinesses } from 'utils/businessUtils';
import { matchPath, useLocation } from 'react-router-dom';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import BusinessPlaylistsDialog from 'components/profile/playlists/BusinessPlaylistsDialog';
import LargeImageGallery from 'components/ui/LargeImageGallery';
import PageContent from 'components/ui/PageContent';
import ProfileBody from 'components/business/landing-page/ProfileBody';
import SaveBusiness from 'components/business/landing-page/SaveBusiness';
import SignInDialog from 'components/sign-in/SignInDialog';
import Wrapper from 'components/Wrapper';
import useLoadPlaylists from 'hooks/useLoadPlaylists';

interface Props {
  business: consumer.BusinessConfig;
  isLoading: boolean;
  nextRelease?: core.IBookingRelease;
  offerings: consumer.ConsumerOfferings;
  onAddTicketTypeToWaitlist(experienceId?: number): void;
  onMobileSearch(): void;
  onSearch(size: number, date: any, time: any): void;
  ticketGroups: consumer.CalendarTicketGroup[];
}

const BusinessProfileContent: React.FunctionComponent<Props> = ({
  business,
  isLoading,
  nextRelease,
  offerings,
  onAddTicketTypeToWaitlist,
  onMobileSearch,
  onSearch,
  ticketGroups,
}) => {
  const { actions } = useActions();
  const sendConversionAnalytics = useConsumerConversionAnalytics();
  const [isPlaylistDialogOpen, setIsPlaylistDialogOpen] = React.useState(false);
  const [isSignInDialogOpen, setIsSignInDialogOpen] = React.useState(false);
  const [prevNavigation, setPrevNavigation] = React.useState<Navigation | undefined>(undefined);
  const associatedBusinesses = getAssociatedBusinesses(business);
  const profileImages = business.profileImage;
  const loggedInPatron = useSelector((state: StoreType) => state.app.patron);
  const navigation = useSelector((state: StoreType) => state.navigation);

  useLoadPlaylists();

  const handleOpenSignInDialog = () => {
    window.analytics.sendEvent('Playlist', 'Log in clicked', 'Log in opened');
    setPrevNavigation(navigation);
    setIsSignInDialogOpen(true);
  };

  const location = useLocation();

  const businessHomePageMatch = !!matchPath(location.pathname, {
    path: `/:domainName(${business.domainName})`,
    exact: true,
  });

  useMountEffect(() => {
    sendConversionAnalytics(ACTION_BUSINESS_VIEW_PAGE);
  });

  return (
    <>
      {businessHomePageMatch ? <BusinessHomepageJsonLd business={business} /> : null}
      <PageContent className="PageContent--page-business" mainFooter>
        <div className="EventDetailContent-smallImageGallery">
          <LargeImageGallery
            show={1}
            business={business}
            images={profileImages as core.UploadedImage[]}
            isLightboxEnabled={profileImages.length > 1}
            onClickImage={() =>
              window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_IMAGE_CLICKED)
            }
            aspect={2.5}
          />

          <div
            css={(theme: Theme) => css`
              display: flex;
              justify-content: flex-end;
              margin-top: ${theme.spacing(-2.75)}px;
              margin-right: ${theme.spacing(1.5)}px;
            `}
          >
            <SaveBusiness
              business={business}
              onOpenDialog={() =>
                !loggedInPatron ? handleOpenSignInDialog() : setIsPlaylistDialogOpen(true)
              }
            />
          </div>
        </div>

        <div className="ProfileBody">
          <Wrapper className="EventDetailContent-largeImageGallery" size="medium">
            <LargeImageGallery
              onClickImage={() =>
                window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_IMAGE_CLICKED)
              }
              business={business}
              show={profileImages.length >= 3 ? 3 : 1}
              images={profileImages as core.UploadedImage[]}
              isLightboxEnabled={profileImages.length > 1}
              aspect={2.5}
            />
          </Wrapper>

          <Wrapper size="medium" inset>
            <ProfileBody
              business={business}
              isLoading={isLoading}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              nextRelease={nextRelease!}
              offerings={offerings}
              onAddTicketTypeToWaitlist={onAddTicketTypeToWaitlist}
              onMobileSearch={onMobileSearch}
              onSearch={onSearch}
              ticketGroups={ticketGroups}
              onOpenPlaylistDialog={() =>
                !loggedInPatron ? handleOpenSignInDialog() : setIsPlaylistDialogOpen(true)
              }
            />
          </Wrapper>
          <Wrapper size="medium" inset>
            <AssociatedBusinesses businesses={associatedBusinesses} />
          </Wrapper>
        </div>
      </PageContent>
      <BusinessPlaylistsDialog
        isOpen={isPlaylistDialogOpen}
        onClose={() => setIsPlaylistDialogOpen(false)}
        business={business}
      />
      <SignInDialog
        isOpen={isSignInDialogOpen}
        onClose={() => {
          setIsSignInDialogOpen(false);
          prevNavigation && actions.navigation.set(prevNavigation);
          setPrevNavigation(undefined);
        }}
        onAuthComplete={(signInMode) => {
          window.analytics.sendEvent(
            'Playlist',
            'Log in clicked',
            signInMode === 'login' ? 'Logged in' : 'Signed up'
          );
          setIsSignInDialogOpen(false);
          setIsPlaylistDialogOpen(true);
        }}
      />
    </>
  );
};

export default BusinessProfileContent;
